import type {
  ChannelQuotaDto,
  ChannelQuotaRequestDto,
  WebWidgetBehaviorRequestDto,
  WebWidgetBehaviorResponseDto,
  WebWidgetDetailResponseDto,
  WebWidgetPublishSettingsRequestDto,
} from './web-widgets.api.types';
import type { WebWidgetBehavior, WebWidgetDetail, WebWidgetFeedbackLimit, WebWidgetPublishSettings } from './types';

// Matches the API defaults of a channel created without a quota. The quota belongs to the channel, so the Mobile SDK
// record shares it.
export function fromApiChannelQuota(quota: ChannelQuotaDto | null): WebWidgetFeedbackLimit {
  return {
    actionKind: quota?.action_kind ?? 'submit',
    active: quota?.active ?? false,
    daily: quota?.daily ?? null,
    hourly: quota?.hourly ?? null,
    id: quota?.id ?? null,
    monthly: quota?.monthly ?? null,
    weekly: quota?.weekly ?? null,
  };
}

export function fromApiWebWidgetBehavior(response: WebWidgetBehaviorResponseDto): WebWidgetBehavior {
  return {
    displayOnce: response.display_once ?? false,
    displayOnceDelayPeriod: response.display_once_delay_period,
    displayRate: response.display_rate,
    enabledOnMobile: response.enabled_on_mobile ?? true,
    feedbackLimit: fromApiChannelQuota(response.channel_quota),
    fullScreenOnMobile: response.full_page_on_mobile,
    initiallyVisible: response.is_visible,
    multipleFeedbackDelayPeriod: response.multiple_feedback_delay_period,
    preventMultipleFeedback: response.prevent_multiple_feedback,
    showOnExit: response.display_on_exit ?? false,
  };
}

export function fromApiWebWidgetDetail(response: WebWidgetDetailResponseDto): WebWidgetDetail {
  return {
    behavior: fromApiWebWidgetBehavior(response),
    code: response.code,
    id: response.id,
    publishing: {
      enabledDomains: response.enabled_domains ?? [],
      enabledUrls: response.enabled_urls,
      secureMode: response.secure_mode,
      securityToken: response.security_token,
    },
  };
}

export function toApiWebWidgetPublishSettings(settings: WebWidgetPublishSettings): WebWidgetPublishSettingsRequestDto {
  return {
    enabled_domains: settings.enabledDomains,
    enabled_urls: settings.enabledUrls,
    secure_mode: settings.secureMode,
  };
}

export function toApiChannelQuota(feedbackLimit: WebWidgetFeedbackLimit): ChannelQuotaRequestDto {
  return {
    action_kind: feedbackLimit.actionKind,
    active: feedbackLimit.active,
    daily: feedbackLimit.daily,
    hourly: feedbackLimit.hourly,
    monthly: feedbackLimit.monthly,
    weekly: feedbackLimit.weekly,
    ...(feedbackLimit.id ? { id: feedbackLimit.id } : {}),
  };
}

export function toApiWebWidgetBehavior(behavior: WebWidgetBehavior): WebWidgetBehaviorRequestDto {
  return {
    channel_quota: toApiChannelQuota(behavior.feedbackLimit),
    display_on_exit: behavior.showOnExit,
    display_once: behavior.displayOnce,
    display_once_delay_period: behavior.displayOnceDelayPeriod,
    display_rate: behavior.displayRate,
    enabled_on_mobile: behavior.enabledOnMobile,
    full_page_on_mobile: behavior.fullScreenOnMobile,
    is_visible: behavior.initiallyVisible,
    multiple_feedback_delay_period: behavior.multipleFeedbackDelayPeriod,
    prevent_multiple_feedback: behavior.preventMultipleFeedback,
  };
}
